import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import ItemCard from "../components/ItemCard";
import ItemDetailDrawer from "../components/ItemDetailDrawer";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Sun, CalendarDays, Clock, Inbox } from "lucide-react";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/dashboard").then(({ data }) => setData(data));
  }, []);

  useEffect(() => { load(); }, [load]);

  // deep-link from a reminder notification: /dashboard?item=<id>
  useEffect(() => {
    const itemId = searchParams.get("item");
    if (itemId) {
      setSelected(itemId);
      searchParams.delete("item");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const hour = new Date().getHours();
  const greetKey = hour < 12 ? "greetingMorning" : hour < 18 ? "greetingAfternoon" : "greetingEvening";
  const firstName = user?.name?.split(" ")[0] || "";

  const sections = [
    { key: "today", title: t("dashboard.sectionToday"), icon: Sun, empty: t("dashboard.emptyToday") },
    { key: "week", title: t("dashboard.sectionWeek"), icon: CalendarDays, empty: t("dashboard.emptyWeek") },
    { key: "upcoming", title: t("dashboard.sectionUpcoming"), icon: Clock, empty: t("dashboard.emptyUpcoming") },
  ];

  const total = data ? data.today.length + data.week.length + data.upcoming.length : 0;

  return (
    <Layout onDataChanged={load}>
      <div className="animate-fade-up">
        <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight">
          {t(`dashboard.${greetKey}`, { name: firstName })}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {total === 0 ? t("dashboard.clearMind") : t("dashboard.tracking", { count: total })}
        </p>
      </div>

      {!data ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : total === 0 ? (
        <div className="mt-16 text-center">
          <div className="h-16 w-16 rounded-2xl bg-accent mx-auto flex items-center justify-center">
            <Inbox className="h-8 w-8 brand-text" />
          </div>
          <p className="mt-4 font-medium">{t("dashboard.emptyTitle")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("dashboard.emptySubtitle")}</p>
        </div>
      ) : (
        <div className="mt-8 space-y-8">
          {sections.map((s) => (
            <section key={s.key} data-testid={`dashboard-section-${s.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <s.icon className="h-4 w-4 brand-text" />
                <h2 className="text-sm font-semibold uppercase tracking-wide font-mono text-muted-foreground">{s.title}</h2>
                <span className="text-xs text-muted-foreground">({data[s.key].length})</span>
              </div>
              {data[s.key].length === 0 ? (
                <p className="text-sm text-muted-foreground pl-6">{s.empty}</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {data[s.key].map((item) => (
                    <ItemCard key={item.id} item={item} onClick={(it) => setSelected(it.id)} />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      <ItemDetailDrawer itemId={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
    </Layout>
  );
}
